import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  MapPin, Clock, Users, ChevronLeft, ChevronRight, Navigation, Shield, Loader2,
  CheckCircle2, XCircle, HelpCircle, ClipboardCheck, UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GameDayPanel({ orgId, teams, userRole, selectedTeamId }) {
  const { isDark } = useTheme();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedGame, setExpandedGame] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});
  const [availabilityData, setAvailabilityData] = useState({});
  const [checkingIn, setCheckingIn] = useState(null);
  const [activeTab, setActiveTab] = useState('roster');

  const text = isDark ? 'text-slate-300' : 'text-slate-600';
  const bg = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const cardBg = isDark ? 'bg-slate-700/40' : 'bg-slate-50';

  const fetchGameDay = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/game-schedule/${orgId}/game-day?date=${viewDate}`;
      if (selectedTeamId) url += `&team_id=${selectedTeamId}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setGames(data.games || []);
    } catch {
      setGames([]);
    }
    setLoading(false);
  }, [orgId, viewDate, selectedTeamId]);

  useEffect(() => { fetchGameDay(); }, [fetchGameDay]);

  const shiftDate = (days) => {
    const d = new Date(viewDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setViewDate(d.toISOString().split('T')[0]);
  };

  const isToday = viewDate === new Date().toISOString().split('T')[0];
  const formatDate = (ds) => {
    const d = new Date(ds + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const openMaps = (address) => {
    if (address) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const fetchAttendance = async (slotId) => {
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/events/${slotId}/attendance`, { withCredentials: true });
      setAttendanceData(prev => ({ ...prev, [slotId]: res.data }));
    } catch { /* ok */ }
  };

  const fetchAvailability = async (slotId) => {
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/event/${slotId}/summary`, { withCredentials: true });
      setAvailabilityData(prev => ({ ...prev, [slotId]: res.data }));
    } catch { /* ok */ }
  };

  const toggleCheckIn = async (slotId, playerId, currentState) => {
    setCheckingIn(playerId);
    try {
      await axios.post(`${API_URL}/api/availability/${orgId}/events/${slotId}/check-in`,
        { player_id: playerId, checked_in: !currentState },
        { withCredentials: true }
      );
      fetchAttendance(slotId);
    } catch (err) { console.error(err); }
    setCheckingIn(null);
  };

  const handleExpand = (idx, game) => {
    if (expandedGame === idx) {
      setExpandedGame(null);
      return;
    }
    setExpandedGame(idx);
    setActiveTab('roster');
    if (game.slot_id) {
      fetchAttendance(game.slot_id);
      fetchAvailability(game.slot_id);
    }
  };

  const canCheckIn = ['ad', 'company_support', 'head_coach', 'assistant_coach'].includes(userRole);

  return (
    <div className="space-y-4" data-testid="game-day-panel">
      <Card className={bg}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <Button size="sm" variant="ghost" onClick={() => shiftDate(-1)} data-testid="gameday-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Input type="date" value={viewDate} onChange={(e) => setViewDate(e.target.value)}
                className={`w-auto text-center text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="gameday-date-input" />
              {!isToday && (
                <Button size="sm" variant="outline" onClick={() => setViewDate(new Date().toISOString().split('T')[0])}
                  className={`text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`} data-testid="gameday-today-btn">Today</Button>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => shiftDate(1)} data-testid="gameday-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <p className={`text-center text-sm mt-1 ${text}`}>{formatDate(viewDate)}</p>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
      ) : games.length === 0 ? (
        <Card className={bg}>
          <CardContent className="p-8 text-center">
            <Shield className={`h-12 w-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {isToday ? 'No games today' : `No games on ${formatDate(viewDate)}`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className={`text-sm font-medium ${text}`}>{games.length} game{games.length !== 1 ? 's' : ''} on this day</p>
          {games.map((game, idx) => {
            const isExpanded = expandedGame === idx;
            const isHome = game.home_away === 'home';
            const att = attendanceData[game.slot_id];
            const avail = availabilityData[game.slot_id];

            return (
              <Card key={`${game.slot_id}-${game.team_slug}`}
                className={`${bg} cursor-pointer transition-all ${isExpanded ? 'ring-2 ring-blue-500/50' : ''}`}
                onClick={() => handleExpand(idx, game)} data-testid={`gameday-card-${idx}`}>
                <CardContent className="p-0">
                  <div className={`px-4 py-2 rounded-t-lg flex items-center justify-between ${
                    isHome ? (isDark ? 'bg-emerald-900/40' : 'bg-emerald-50') : (isDark ? 'bg-red-900/30' : 'bg-red-50')
                  }`}>
                    <span className={`text-xs font-bold uppercase tracking-wider ${isHome ? 'text-emerald-500' : 'text-red-500'}`}>
                      {isHome ? 'Home' : 'Away'}
                    </span>
                    <div className="flex items-center gap-2">
                      {avail && (
                        <div className="flex items-center gap-1 text-[10px]">
                          <span className="text-green-500 font-bold">{avail.yes || 0}Y</span>
                          <span className="text-yellow-500 font-bold">{avail.maybe || 0}M</span>
                          <span className="text-red-500 font-bold">{avail.no || 0}N</span>
                        </div>
                      )}
                      <Badge className={`text-[10px] ${
                        game.status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                        game.status === 'completed' ? 'bg-blue-500/20 text-blue-500' :
                        'bg-amber-500/20 text-amber-500'
                      }`}>{game.status}</Badge>
                    </div>
                  </div>

                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>{game.team_name}</p>
                        <p className={`text-sm ${text}`}>vs <span className="font-semibold">{game.opponent}</span></p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold tabular-nums ${isDark ? 'text-white' : 'text-slate-900'}`}>{game.time}</p>
                        {att && (
                          <p className="text-xs text-green-500 flex items-center justify-end gap-1">
                            <UserCheck className="h-3 w-3" /> {att.checked_in}/{att.total} checked in
                          </p>
                        )}
                      </div>
                    </div>

                    <div className={`flex items-start gap-2 ${text}`}>
                      <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{game.venue || 'Venue TBD'}</p>
                        {game.venue_address && (
                          <button onClick={(e) => { e.stopPropagation(); openMaps(game.venue_address); }}
                            className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1 mt-0.5" data-testid={`gameday-map-${idx}`}>
                            <Navigation className="h-3 w-3" /> Get Directions
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'}`} onClick={e => e.stopPropagation()}>
                        <div className="flex gap-2 mb-3">
                          {['roster', 'checkin', 'lineup'].map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                activeTab === tab
                                  ? 'bg-blue-600 text-white'
                                  : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`} data-testid={`gameday-tab-${tab}`}>
                              {tab === 'roster' && <><Users className="h-3 w-3 inline mr-1" />Roster</>}
                              {tab === 'checkin' && <><ClipboardCheck className="h-3 w-3 inline mr-1" />Check-In</>}
                              {tab === 'lineup' && <><Shield className="h-3 w-3 inline mr-1" />Lineup</>}
                            </button>
                          ))}
                        </div>

                        {/* ROSTER TAB */}
                        {activeTab === 'roster' && (
                          game.roster?.length > 0 ? (
                            <div className="grid grid-cols-2 gap-1">
                              {game.roster.map((p, pi) => (
                                <div key={pi} className={`text-xs p-1.5 rounded ${cardBg} ${text} flex items-center gap-1`}>
                                  {p.jersey_number && <span className={`font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>#{p.jersey_number}</span>}
                                  <span className="truncate">{p.first_name} {p.last_name}</span>
                                </div>
                              ))}
                            </div>
                          ) : <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No roster data</p>
                        )}

                        {/* CHECK-IN TAB */}
                        {activeTab === 'checkin' && (
                          <div className="space-y-1">
                            {att && att.total > 0 ? (
                              <>
                                <div className={`flex items-center justify-between mb-2 text-xs ${text}`}>
                                  <span>{att.checked_in} of {att.total} checked in</span>
                                  <div className="w-32 bg-slate-200 dark:bg-slate-600 rounded-full h-1.5">
                                    <div className="bg-green-500 h-1.5 rounded-full" style={{width: `${att.total > 0 ? (att.checked_in/att.total*100) : 0}%`}} />
                                  </div>
                                </div>
                                {att.records?.map((r) => (
                                  <div key={r.player_id} className={`flex items-center justify-between p-1.5 rounded text-xs ${cardBg}`}>
                                    <div className="flex items-center gap-1.5">
                                      {r.checked_in ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <XCircle className="h-3.5 w-3.5 text-slate-400" />}
                                      <span className={isDark ? 'text-white' : 'text-slate-800'}>{r.jersey_number && `#${r.jersey_number} `}{r.player_name}</span>
                                    </div>
                                    {canCheckIn && (
                                      <Button size="sm" variant="ghost" className={`h-5 px-2 text-[10px] ${r.checked_in ? 'text-red-400' : 'text-green-500'}`}
                                        onClick={() => toggleCheckIn(game.slot_id, r.player_id, r.checked_in)}
                                        disabled={checkingIn === r.player_id} data-testid={`checkin-${r.player_id}`}>
                                        {checkingIn === r.player_id ? <Loader2 className="h-3 w-3 animate-spin" /> : r.checked_in ? 'Undo' : 'Check In'}
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </>
                            ) : game.roster?.length > 0 ? (
                              <div className="space-y-1">
                                <p className={`text-xs mb-2 ${text}`}>No check-in data yet. Tap to check in players:</p>
                                {game.roster.map((p, pi) => (
                                  <div key={pi} className={`flex items-center justify-between p-1.5 rounded text-xs ${cardBg}`}>
                                    <span className={isDark ? 'text-white' : 'text-slate-800'}>{p.jersey_number && `#${p.jersey_number} `}{p.first_name} {p.last_name}</span>
                                    {canCheckIn && (
                                      <Button size="sm" variant="ghost" className="h-5 px-2 text-[10px] text-green-500"
                                        onClick={() => toggleCheckIn(game.slot_id, p.id || `player-${pi}`, false)} data-testid={`checkin-roster-${pi}`}>
                                        Check In
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No roster data for check-in</p>}
                          </div>
                        )}

                        {/* LINEUP TAB */}
                        {activeTab === 'lineup' && (
                          game.lineup?.length > 0 ? (
                            <div className="space-y-1">
                              <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Starting Lineup</p>
                              <div className="flex flex-wrap gap-1">
                                {game.lineup.map((s, si) => <Badge key={si} variant="outline" className="text-[10px]">{s}</Badge>)}
                              </div>
                            </div>
                          ) : (
                            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No lineup set. Create one from the Lineup tab.</p>
                          )
                        )}

                        {game.notes && (
                          <p className={`text-xs mt-2 italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{game.notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
